import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import type {
  CreateProductData,
  Product,
  ProductBusinessModel,
  ProductQueryParams,
  ProductSource,
  UpdateProductData
} from '@/types/product';
import {productService} from '@/services/productService';


interface ProductState {
  items: Product[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  loading: boolean;
  error: string | null;
}

const initialState: ProductState = {
  items: [],
  total: 0,
  page: 1,
  pageSize: 10,
  totalPages: 0,
  loading: false,
  error: null,
};

// 异步 action
export const fetchProducts = createAsyncThunk(
  'product/fetchProducts',
  async (params: ProductQueryParams) => {
    const response = await productService.getProducts(params);
    return response;
  }
);

export const createProduct = createAsyncThunk(
  'product/createProduct',
  async (data: CreateProductData) => {
    const response = await productService.createProduct(data, data.businessModel);
    return response;
  }
);

export const updateProduct = createAsyncThunk(
  'product/updateProduct',
  async ({ id, data }: { id: number; data: UpdateProductData }) => {
    const response = await productService.updateProduct(id, data, data.businessModel);
    return response;
  }
);

export const deleteProduct = createAsyncThunk(
  'product/deleteProduct',
  async ({ id, productSource, businessModel }: { id: number; productSource: ProductSource; businessModel?: ProductBusinessModel }) => {
    if (productSource === 'PLATFORM') {
      await productService.deletePlatformProduct(id, businessModel);
    } else {
      await productService.deleteMerchantProduct(id, businessModel);
    }
    return id;
  }
);

export const onlineProduct = createAsyncThunk(
  'product/onlineProduct',
  async ({ id, businessModel }: { id: number; businessModel?: ProductBusinessModel }) => {
    await productService.onlineProduct(id, businessModel);
    return id;
  }
);

export const offlineProduct = createAsyncThunk(
  'product/offlineProduct',
  async ({ id, productSource, businessModel }: { id: number; productSource: ProductSource; businessModel?: ProductBusinessModel }) => {
    if (productSource === 'PLATFORM') {
      await productService.offlinePlatformProduct(id, businessModel);
    } else {
      await productService.offlineMerchantProduct(id, businessModel);
    }
    return id;
  }
);

// 新增：同步商品到支付宝
export const syncProductToAlipay = createAsyncThunk(
  'product/syncProductToAlipay',
  async ({ id, businessModel }: { id: number; businessModel?: ProductBusinessModel }) => {
    await productService.syncProductToAlipay(id, businessModel);
    return id;
  }
);

export const syncProductToJd = createAsyncThunk(
  'product/syncProductToJd',
  async (id: number) => {
    await productService.syncProductToJd(id);
    return id;
  }
);

export const onlineJdProduct = createAsyncThunk(
  'product/onlineJdProduct',
  async (id: number) => {
    await productService.onlineJdProduct(id);
    return id;
  }
);

export const offlineJdProduct = createAsyncThunk(
  'product/offlineJdProduct',
  async (id: number) => {
    await productService.offlineJdProduct(id);
    return id;
  }
);

const productSlice = createSlice({
  name: 'product',
  initialState,
  reducers: {
    setPagination: (state, action) => {
      if (state.page !== action.payload.page || state.pageSize !== action.payload.pageSize) {
        state.page = action.payload.page;
        state.pageSize = action.payload.pageSize;
      }
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // 获取商品列表
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.pageSize = action.payload.size;
        state.totalPages = action.payload.totalPages;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '获取商品列表失败';
      })
      // 创建商品
      .addCase(createProduct.fulfilled, (state, action) => {
        const newProduct = {
          ...action.payload,
          status: 'DRAFT' as const
        };
        state.items.push(newProduct);
        state.total += 1;
      })
      // 更新商品
      .addCase(updateProduct.fulfilled, (state, action) => {
        const index = state.items.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = {
            ...action.payload,
            status: action.payload.status || ('DRAFT' as const)
          };
        }
      })
      // 删除商品
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.items = state.items.filter(item => item.id !== action.payload);
        state.total -= 1;
      })
      // 上架商品
      .addCase(onlineProduct.fulfilled, (state, action) => {
        const product = state.items.find(item => item.id === action.payload);
        if (product) {
          product.status = 'ONLINE';
        }
      })
      // 下架商品
      .addCase(offlineProduct.fulfilled, (state, action) => {
        const product = state.items.find(item => item.id === action.payload);
        if (product) {
          product.status = 'OFFLINE';
        }
      });
  },
});

export const { setPagination, clearError } = productSlice.actions;
export default productSlice.reducer;
